import { useCallback, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import { NextRouter, useRouter } from "next/router";
import { subscribe } from "valtio";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { IExtendedSearchData } from "src/api/server/extended-search";
import { mapStateToQueryString } from "src/components/pages/search/utils/map-state-to-query-string";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { ISearchStore, searchStore } from "../store/search-store";

export function useOnSearchStoreChange(data: IExtendedSearchData) {
    const WAIT_MILLISECONDS = 650;
    const MAX_WAIT = 350;

    const router = useRouter();
    const { country } = useRouterLocale();
    const genericActivity = useGenericActivityFromElysiumMutation();

    const updateUrl = useCallback(
        (state: ISearchStore, router: NextRouter) => {
            const { origin, pathname, href } = window.location;
            const urlParams = new URLSearchParams(window.location.search);
            const caller = urlParams.get("caller");
            const queryString = mapStateToQueryString(state, data, country, caller);
            router.replace({ pathname, query: queryString, href }, undefined, { shallow: true });
            genericActivity({
                activityTypeId: EnumActivityType.VisitWikifolioSearchPage,
                parameter: queryString,
                referrer: `${origin}${pathname}?${queryString}`,
            });
        },
        [country, data, genericActivity]
    );

    const updateUrlDebounced = useMemo(() => debounce(updateUrl, WAIT_MILLISECONDS, { leading: false, maxWait: MAX_WAIT }), [updateUrl]);

    useEffect(() => {
        const unsubscribe = subscribe(searchStore, () => {
            updateUrlDebounced(searchStore, router);
        });

        return () => unsubscribe();
    }, [router, updateUrlDebounced]);
}
